import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow, Button, HUDBrackets } from './UI';
type Hotspot = {
  id: string;
  tag: string;
  name: string;
  desc: string;
  // Position on image (percentages)
  top: string;
  left: string;
  // Side the label sits on
  side: 'left' | 'right';
};
type HotspotExt = Hotspot & {
  specs: {
    label: string;
    value: string;
  }[];
};
const hotspots: HotspotExt[] = [{
  id: 'box',
  tag: '01',
  name: 'TACTICAL FIELD BOX',
  desc: 'Camo-printed collector packaging engineered to protect in transit and display with authority. Every BLACKOUT UNIT ships in a custom-built box designed to look as serious as what is inside.',
  top: '22%',
  left: '28%',
  side: 'left',
  specs: [{
    label: 'MATERIAL',
    value: 'REINFORCED CARDBOARD'
  }, {
    label: 'FINISH',
    value: 'MILITARY CAMO PRINT'
  }, {
    label: 'PURPOSE',
    value: 'SHIP + DISPLAY'
  }]
}, {
  id: 'mask',
  tag: '02',
  name: 'BLACKOUT UNIT MASK™',
  desc: 'The core unit. A glass-fiber composite shell wrapped around a Grade 304 stainless steel skeleton. Built to absorb impact, resist deformation, and operate in any theater you deploy it into.',
  top: '52%',
  left: '72%',
  side: 'right',
  specs: [{
    label: 'SHELL',
    value: 'GLASS-FIBER COMPOSITE'
  }, {
    label: 'FRAME',
    value: 'GRADE 304 STAINLESS'
  }, {
    label: 'RATING',
    value: 'BALLISTIC + TACTICAL'
  }]
}, {
  id: 'lenses',
  tag: '03',
  name: 'SMOKE BLACK LENSES',
  desc: 'Pre-installed polycarbonate lenses rated to ANSI Z87.1 — the same standard used in industrial impact protection. Anti-fog coating. UV400 filtering. Field-swappable in under 30 seconds.',
  top: '40%',
  left: '78%',
  side: 'right',
  specs: [{
    label: 'MATERIAL',
    value: 'POLYCARBONATE'
  }, {
    label: 'STANDARD',
    value: 'ANSI Z87.1'
  }, {
    label: 'COATING',
    value: 'ANTI-FOG · UV400'
  }]
}, {
  id: 'strap',
  tag: '04',
  name: 'REINFORCED NYLON STRAP',
  desc: 'Heavy-duty dual-point adjustable strap. Engineered to hold zero-wobble lock across head circumferences from 52cm to 62cm. Fits adults and youth users alike.',
  top: '68%',
  left: '22%',
  side: 'left',
  specs: [{
    label: 'MATERIAL',
    value: 'REINFORCED NYLON'
  }, {
    label: 'ADJUST',
    value: 'DUAL-POINT'
  }, {
    label: 'RANGE',
    value: '52CM – 62CM'
  }]
}, {
  id: 'padding',
  tag: '05',
  name: 'NEOPRENE COMFORT PADDING',
  desc: 'Military-spec interior padding. Anti-bacterial. Moisture-wicking. Engineered for hour-long operations without pressure-point fatigue. Replaceable. Field-cleanable.',
  top: '82%',
  left: '60%',
  side: 'right',
  specs: [{
    label: 'MATERIAL',
    value: 'MIL-SPEC NEOPRENE'
  }, {
    label: 'TREATMENT',
    value: 'ANTI-BACTERIAL'
  }, {
    label: 'PROPERTY',
    value: 'MOISTURE-WICKING'
  }]
}];
export const WhatsInBox = () => {
  const [activeId, setActiveId] = useState<string>('mask');
  return <section className="bg-gradient-to-b from-dark-1 to-base-black pt-[40px] pb-[40px]" data-id="element-1563">
      <div className="container mx-auto px-6 md:px-12" data-id="element-1564">
        <div className="text-center mb-16" data-id="element-1565">
          <Eyebrow data-id="element-1566">FULL KIT INVENTORY</Eyebrow>
          <h2 className="text-5xl md:text-[64px] leading-[0.9] mb-4" data-id="element-1567">
            EVERYTHING THAT SHIPS WITH YOUR{' '}
            <span className="text-acid-green" data-id="element-1568">UNIT</span>.
          </h2>
          <p className="font-barlow text-lg text-text-body max-w-2xl mx-auto" data-id="element-1569">
            Mission-ready out of the box. Hover or tap any marker to inspect the
            kit.
          </p>
        </div>

        {/* Interactive Hotspot Diagram */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-10 lg:gap-16 max-w-[1200px] mx-auto items-center mb-20" data-id="element-1570">
          {/* Image with hotspots */}
          <div className="relative border border-acid-green/20 bg-dark-1 aspect-square overflow-hidden" data-id="element-1571">
            <HUDBrackets data-id="element-1572" />
            <div className="absolute top-4 left-4 font-barlow text-[10px] text-acid-green tracking-[2px] z-20 border border-acid-green/40 px-2 py-1 bg-base-black/60" data-id="element-1573">
              KIT INSPECTION — 05 ITEMS
            </div>

            <img src="https://cdn.magicpatterns.com/uploads/7fnAZ9tC6TNigxzBPNFtQB/hf_20260505_161401_b22ab001-c648-4408-a098-97bb18fba3a3.png" alt="BLACKOUT UNIT Mask & Box Kit" className="absolute inset-0 w-full h-full object-cover" data-id="element-1574" />
            {/* Subtle vignette */}
            <div className="absolute inset-0 bg-gradient-to-t from-base-black/50 via-transparent to-base-black/20 pointer-events-none" data-id="element-1575" />

            {/* Dynamic Highlight Aura */}
            {hotspots.map(h => {
            const isActive = activeId === h.id;
            return <AnimatePresence key={`aura-${h.id}`} data-id="element-1576">
                  {isActive && <motion.div initial={{
                opacity: 0,
                scale: 0.5
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.5
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} className="absolute z-0 pointer-events-none" style={{
                top: `calc(${h.top} - 25%)`,
                left: `calc(${h.left} - 25%)`,
                width: '50%',
                height: '50%',
                background: 'radial-gradient(circle, rgba(200,255,0,0.35) 0%, rgba(200,255,0,0.1) 30%, transparent 70%)',
                mixBlendMode: 'screen'
              }} data-id="element-1577" />}
                </AnimatePresence>;
          })}

            {/* Hotspots */}
            {hotspots.map(h => {
            const isActive = activeId === h.id;
            return <button key={h.id} onMouseEnter={() => setActiveId(h.id)} onClick={() => setActiveId(h.id)} className="absolute z-10 -translate-x-1/2 -translate-y-1/2 group" style={{
              top: h.top,
              left: h.left
            }} aria-label={h.name} data-id="element-1578">
                  {/* Pulse ring */}
                  <span className={`absolute inset-0 m-auto w-8 h-8 rounded-full border-2 border-acid-green ${isActive ? 'animate-ping opacity-60' : 'opacity-0'}`} data-id="element-1579" />
                  {/* Outer ring */}
                  <span className={`relative block w-8 h-8 rounded-full border-2 transition-all ${isActive ? 'border-acid-green bg-acid-green/20 scale-110' : 'border-white/70 bg-black/40 group-hover:border-acid-green'}`} data-id="element-1580">
                    {/* Inner dot */}
                    <span className={`absolute inset-0 m-auto w-3 h-3 rounded-full transition-colors ${isActive ? 'bg-acid-green' : 'bg-white group-hover:bg-acid-green'}`} style={{
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)'
                }} data-id="element-1581" />
                  </span>
                  {/* Tag number floating above */}
                  <span className={`absolute -top-6 left-1/2 -translate-x-1/2 font-bebas text-[14px] tracking-[2px] transition-colors ${isActive ? 'text-acid-green' : 'text-white/80'}`} data-id="element-1582">
                    {h.tag}
                  </span>
                </button>;
          })}
          </div>

          {/* Right: Immersive Intel Readout Panel */}
          <div className="relative bg-dark-1 border border-dark-3 overflow-hidden" data-id="element-1583">
            {/* Background Image — Tactical Operator (visible across all 5 items) */}
            <img src="https://cdn.magicpatterns.com/uploads/styiL95jqae3dHQjsU8SNs/hf_20260519_153516_d713b8fb-1d1a-416c-9a61-f78daa6411f2_(1).png" alt="Tactical Operator wearing BLACKOUT UNIT mask" className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none" data-id="element-1584" />
            <div className="absolute inset-0 bg-base-black/40 pointer-events-none" data-id="element-1585"></div>
            <div className="absolute inset-0 bg-gradient-to-r from-base-black/85 via-base-black/45 to-base-black/15 pointer-events-none" data-id="element-1586"></div>

            {/* Corner brackets */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-acid-green/50 pointer-events-none z-10" data-id="element-1587"></div>
            <div className="absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-acid-green/50 pointer-events-none z-10" data-id="element-1588"></div>
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-acid-green/50 pointer-events-none z-10" data-id="element-1589"></div>
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-acid-green/50 pointer-events-none z-10" data-id="element-1590"></div>

            {/* Status Header Bar */}
            <div className="relative flex items-center justify-between px-5 py-3 border-b border-dark-3 bg-base-black/60 backdrop-blur-sm z-10" data-id="element-1591">
              <div className="flex items-center gap-2" data-id="element-1592">
                <span className="w-1.5 h-1.5 bg-acid-green animate-pulse" data-id="element-1593"></span>
                <span className="font-barlow font-bold text-[10px] text-acid-green tracking-[3px] uppercase" data-id="element-1594">
                  Intel Readout — Active
                </span>
              </div>
              <span className="font-barlow text-[10px] text-text-dimmed tracking-[2px]" data-id="element-1595">
                ITEM {hotspots.find(h => h.id === activeId)?.tag} / 05
              </span>
            </div>

            {/* Dynamic Content */}
            <div className="relative p-6 min-h-[380px] z-10" data-id="element-1596">
              <AnimatePresence mode="wait" data-id="element-1597">
                {hotspots.filter(h => h.id === activeId).map(h => <motion.div key={h.id} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.4,
                ease: 'easeOut'
              }} data-id="element-1598">
                      {/* Item Tag Number (large) */}
                      <div className="flex items-baseline gap-3 mb-4" data-id="element-1599">
                        <span className="font-bebas text-6xl text-acid-green leading-none" data-id="element-1600">
                          {h.tag}
                        </span>
                        <span className="font-barlow text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-1601">
                          Designation
                        </span>
                      </div>

                      {/* Title - animated word stagger */}
                      <motion.h3 className="font-bebas text-3xl md:text-4xl text-white leading-tight mb-4 tracking-wide" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.15,
                  duration: 0.4
                }} data-id="element-1602">
                        {h.name}
                      </motion.h3>

                      {/* Description - word by word fade-in like a typewriter */}
                      <motion.p className="font-inter text-sm text-text-body leading-relaxed mb-6" initial="hidden" animate="visible" variants={{
                  hidden: {},
                  visible: {
                    transition: {
                      staggerChildren: 0.02,
                      delayChildren: 0.25
                    }
                  }
                }} data-id="element-1603">
                        {h.desc.split(' ').map((word, i) => <motion.span key={i} variants={{
                    hidden: {
                      opacity: 0,
                      y: 5
                    },
                    visible: {
                      opacity: 1,
                      y: 0
                    }
                  }} className="inline-block mr-1" data-id="element-1604">
                            {word}
                          </motion.span>)}
                      </motion.p>

                      {/* Specs Grid */}
                      <motion.div className="grid grid-cols-1 gap-2 pt-4 border-t border-dark-3" initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} transition={{
                  delay: 0.6,
                  duration: 0.4
                }} data-id="element-1605">
                        {h.specs.map((spec, i) => <motion.div key={spec.label} initial={{
                    opacity: 0,
                    x: -10
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} transition={{
                    delay: 0.7 + i * 0.08,
                    duration: 0.3
                  }} className="flex items-center justify-between py-2 border-b border-dark-3/50 last:border-b-0" data-id="element-1606">
                            <span className="font-barlow font-bold text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-1607">
                              {spec.label}
                            </span>
                            <span className="font-bebas text-sm text-acid-green tracking-wide" data-id="element-1608">
                              {spec.value}
                            </span>
                          </motion.div>)}
                      </motion.div>
                    </motion.div>)}
              </AnimatePresence>
            </div>

            {/* Bottom Navigation Dots */}
            <div className="relative border-t border-dark-3 bg-base-black/60 backdrop-blur-sm px-5 py-3 flex items-center justify-between z-10" data-id="element-1609">
              <span className="font-barlow text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-1610">
                Browse Kit
              </span>
              <div className="flex gap-2" data-id="element-1611">
                {hotspots.map(h => {
                const isActive = activeId === h.id;
                return <button key={h.id} onClick={() => setActiveId(h.id)} onMouseEnter={() => setActiveId(h.id)} aria-label={`Show ${h.name}`} className={`relative font-bebas text-xs tracking-wider w-7 h-7 border flex items-center justify-center transition-all ${isActive ? 'border-acid-green text-acid-green bg-acid-green/10' : 'border-dark-3 text-text-dimmed hover:border-acid-green/40 hover:text-white'}`} data-id="element-1612">
                      {h.tag}
                      {isActive && <motion.span layoutId="kit-active-indicator" className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-acid-green rounded-full" data-id="element-1613" />}
                    </button>;
              })}
              </div>
            </div>
          </div>
        </div>

        {/* Value Block */}
      </div>
    </section>;
};